import { Link } from "react-router-dom";
import { Moon, Sun } from "lucide-react";
import { cn } from "@/lib/utils";

export const Logo = ({ className, light, to = "/" }) => (
  <Link to={to} data-testid="brand-logo" aria-label="Saflr home" className={cn("inline-flex items-center p-[5px]", className)}>
    {light ? <img src="/brand/logo-light.png" alt="Saflr" className="h-8 w-auto" /> : (
      <>
        <img src="/brand/logo-dark.png" alt="Saflr" className="h-8 w-auto dark:hidden" />
        <img src="/brand/logo-light.png" alt="Saflr" className="hidden h-8 w-auto dark:block" />
      </>
    )}
  </Link>
);

const STATUS = {
  draft: ["Draft", "bg-slate-500/10 text-slate-600 dark:text-slate-300 ring-slate-500/20"],
  under_review: ["Under review", "bg-[#FFA000]/10 text-[#B87300] dark:text-[#FFB733] ring-[#FFA000]/30"],
  approved: ["Approved", "bg-health/10 text-[#00876C] dark:text-health ring-health/30"],
  issued: ["Issued", "bg-navy/10 text-navy dark:bg-white/10 dark:text-white ring-navy/20 dark:ring-white/20"],
  superseded: ["Superseded", "bg-alert/10 text-alert ring-alert/30"],
  archived: ["Archived", "bg-slate-500/10 text-slate-500 ring-slate-500/20"],
  review_due: ["Review due", "bg-alert/10 text-alert ring-alert/30"],
};

export const StatusBadge = ({ status, testid }) => {
  const [label, cls] = STATUS[status] || [status, STATUS.draft[1]];
  return <span data-testid={testid || `status-badge-${status}`} className={cn("inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-medium ring-1", cls)}><span className="h-1.5 w-1.5 rounded-full bg-current" />{label}</span>;
};

export const RiskBadge = ({ score }) => {
  const lvl = score <= 4 ? ["Low", "bg-health text-navy"] : score <= 9 ? ["Med", "bg-[#FFA000] text-navy"] : score <= 15 ? ["High", "bg-[#F2611D] text-white"] : ["V.High", "bg-alert text-white"];
  return <span className={cn("inline-flex min-w-[52px] justify-center rounded-md px-2 py-1 font-mono text-xs font-semibold", lvl[1])}>{score} {lvl[0]}</span>;
};

export const Loader = ({ label = "Loading" }) => (
  <div data-testid="saflr-loader" className="flex flex-col items-center justify-center gap-4 py-24">
    <div className="flex gap-1.5">
      {["bg-health", "bg-[#FFA000]", "bg-alert"].map((c, i) => <span key={c} className={cn("h-2.5 w-2.5 rounded-full animate-bounce", c)} style={{ animationDelay: `${i * 120}ms` }} />)}
    </div>
    <p className="font-mono text-xs uppercase tracking-widest text-muted-foreground">{label}</p>
  </div>
);

export const EmptyState = ({ icon: Icon, title, text, action }) => (
  <div data-testid="empty-state" className="flex flex-col items-center rounded-2xl border border-dashed p-12 text-center">
    {Icon && <div className="mb-4 grid h-14 w-14 place-items-center rounded-2xl bg-health/10 text-health"><Icon className="h-6 w-6" /></div>}
    <h3 className="text-lg font-semibold">{title}</h3>
    <p className="mt-1 max-w-sm text-sm text-muted-foreground">{text}</p>
    {action && <div className="mt-6">{action}</div>}
  </div>
);

export const ThemeToggle = ({ theme, setTheme, className }) => (
  <button data-testid="theme-toggle" aria-label="Toggle theme" onClick={() => setTheme(theme === "dark" ? "light" : "dark")} className={cn("grid h-9 w-9 place-items-center rounded-lg border transition-colors hover:bg-accent", className)}>
    {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
  </button>
);

export const Stat = ({ label, value, hint, tone = "navy", testid, icon: Icon }) => {
  const tones = { green: "text-health", amber: "text-[#FFA000]", red: "text-alert", navy: "text-foreground" };
  return (
    <div data-testid={testid} className="card-lift rounded-2xl border bg-card p-5">
      <div className="flex items-center justify-between">
        <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">{label}</p>
        {Icon && <Icon className={cn("h-4 w-4", tones[tone])} />}
      </div>
      <p className={cn("mt-3 font-display text-3xl font-semibold", tones[tone])}>{value}</p>
      {hint && <p className="mt-1 text-xs text-muted-foreground">{hint}</p>}
    </div>
  );
};

export const Seo = ({ title, description }) => {
  if (typeof document !== "undefined") {
    document.title = title || "Saflr | Safety. Simplified.";
    const m = document.querySelector('meta[name="description"]');
    if (m && description) m.setAttribute("content", description);
  }
  return null;
};
